import React, { useState, useEffect } from 'react';
import { Check, Crown, Upload, HardDrive, Activity, Star } from 'lucide-react';
import UserRegistrationModal from '../components/UserRegistrationModal';
import CheckoutModal from '../components/CheckoutModal';
import { useAuth } from '../contexts/AuthContext';

const PricingPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  
  const { user, isAuthenticated } = useAuth();

  // Static UI for now (backend checkout will be wired later)
  useEffect(() => { setLoading(false); }, []);

  const handleUpgrade = async (planId) => {
    try {
      // Get current user ID from localStorage
      const userId = localStorage.getItem('fileshare_user_id');
      if (!userId) {
        alert('Please refresh the page and try again');
        return;
      }

      // Check if user is authenticated
      if (!isAuthenticated || !user) {
        // Show registration modal
        setSelectedPlanId(planId);
        setShowRegistrationModal(true);
        return;
      }

      // User is authenticated, proceed to checkout
      setSelectedPlanId(planId);
      setShowCheckoutModal(true);
      
    } catch (error) {
      console.error('Error initiating upgrade:', error);
      alert('Failed to initiate upgrade. Please try again.');
    }
  };

  const handleRegistrationSuccess = (firebaseUser) => {
    setShowRegistrationModal(false);
    setShowCheckoutModal(true);
  };

  const handleCheckoutClose = () => {
    setShowCheckoutModal(false);
    setSelectedPlanId(null);
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const gb = bytes / (1024 * 1024 * 1024);
    return `${Math.round(gb)}GB`;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const gb = bytes / (1024 * 1024 * 1024);
    return `${Math.round(gb)}GB`;
  };

  if (loading) {
    return (
      <div style={{ backgroundColor: '#1a1d29', color: '#e2e8f0', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '48px', 
            height: '48px', 
            border: '3px solid #374151', 
            borderTop: '3px solid #3b82f6', 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#9ca3af', fontSize: '14px' }}>Loading pricing plans...</p>
        </div>
      </div>
    );
  }

  // Ignore error state for static UI

  return (
    <div style={{ backgroundColor: '#1a1d29', color: '#e2e8f0', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '64px 16px' }}>
        <div style={{ textAlign: 'center', marginBottom: '64px' }}>
          <h1 style={{ fontSize: '20px', fontWeight: '600', color: '#e2e8f0', marginBottom: '8px', letterSpacing: '-0.02em' }}>
            Simple, powerful pricing
          </h1>
          <p style={{ fontSize: '12px', color: '#9ca3af', maxWidth: '600px', margin: '0 auto' }}>
            Premium includes unlimited file size, 500GB storage and 1TB bandwidth. You only pay for extra usage beyond these limits.
          </p>
        </div>

        {/* Pricing Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', maxWidth: '800px', margin: '0 auto' }}>

          {/* Free Plan */}
          <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '3px', padding: '24px', position: 'relative' }}>
            <div style={{ textAlign: 'center' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#e2e8f0', marginBottom: '8px' }}>Free</h3>
              <p style={{ color: '#9ca3af', marginBottom: '16px', fontSize: '14px' }}>Perfect for small transfers</p>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3b82f6', marginBottom: '8px' }}>$0</div>
              <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '24px' }}>forever</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Check style={{ height: '16px', width: '16px', color: '#10b981', marginRight: '12px' }} />
                <span style={{ color: '#d1d5db', fontSize: '14px' }}>Upload any size (quota applies)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Check style={{ height: '16px', width: '16px', color: '#10b981', marginRight: '12px' }} />
                <span style={{ color: '#d1d5db', fontSize: '14px' }}>Basic file sharing</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Check style={{ height: '16px', width: '16px', color: '#10b981', marginRight: '12px' }} />
                <span style={{ color: '#d1d5db', fontSize: '14px' }}>5 days expiry</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Check style={{ height: '16px', width: '16px', color: '#10b981', marginRight: '12px' }} />
                <span style={{ color: '#d1d5db', fontSize: '14px' }}>100GB monthly bandwidth (uploads + downloads)</span>
              </div>
            </div>
            <button 
              style={{ 
                backgroundColor: '#374151', 
                color: '#9ca3af', 
                padding: '12px 24px', 
                borderRadius: '3px',
                border: '1px solid #4b5563',
                cursor: 'not-allowed',
                fontSize: '14px',
                fontWeight: '500',
                width: '100%'
              }}
              disabled
            >
              Current Plan
            </button>
          </div>
          {/* Premium Plan */}
          <div style={{ backgroundColor: '#242938', border: '2px solid #16a34a', borderRadius: '8px', padding: '24px', position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', justifyContent: 'center' }}>
              <Crown style={{ height: 18, width: 18, color: '#16a34a' }} />
              <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#e2e8f0', margin: 0 }}>Premium</h3>
            </div>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#16a34a', marginBottom: '6px' }}>$8</div>
              <div style={{ fontSize: '12px', color: '#9ca3af' }}>per month</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Upload style={{ height: 16, width: 16, color: '#16a34a', marginRight: 12, fill: '#16a34a' }} />
                <span style={{ color: '#d1d5db', fontSize: 14 }}>Unlimited File Size</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <HardDrive style={{ height: 16, width: 16, color: '#16a34a', marginRight: 12, fill: '#16a34a' }} />
                <span style={{ color: '#d1d5db', fontSize: 14 }}>500GB Storage Quota</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Activity style={{ height: 16, width: 16, color: '#16a34a', marginRight: 12, fill: '#16a34a' }} />
                <span style={{ color: '#d1d5db', fontSize: 14 }}>1TB Monthly Bandwidth (uploads + downloads)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Star style={{ height: 16, width: 16, color: '#16a34a', marginRight: 12, fill: '#16a34a' }} />
                <span style={{ color: '#d1d5db', fontSize: 14 }}>All Premium Features</span>
              </div>
            </div>
            <button 
              onClick={() => handleUpgrade('premium')}
              style={{ backgroundColor: '#16a34a', color: 'white', padding: '12px 24px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700, width: '100%' }}>
              Upgrade to Premium
            </button>
          </div>
          {/* Removed add-ons card as requested */}
        </div>

        {/* FAQs Section */}
        <div style={{ marginTop: '48px', maxWidth: '800px', margin: '48px auto 0' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#e2e8f0', textAlign: 'center', marginBottom: '32px' }}>
            Frequently Asked Questions
          </h2>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* FAQ 1 */}
            <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '8px', padding: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#e2e8f0', marginBottom: '12px' }}>
                What happens if I exceed 500GB storage or 1TB bandwidth?
              </h3>
              <p style={{ color: '#d1d5db', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                Don't worry! If you exceed your included limits, you only pay for the extra usage. 
                For example, if you use 550GB storage, you'll only be charged for the extra 50GB. 
                The overage charges are automatically added to your next billing cycle.
              </p>
            </div>

            {/* FAQ 2 */}
            <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '8px', padding: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#e2e8f0', marginBottom: '12px' }}>
                What are the overage rates?
              </h3>
              <div style={{ color: '#d1d5db', fontSize: '14px', lineHeight: '1.6' }}>
                <p style={{ margin: '0 0 8px 0' }}>
                  <strong style={{ color: '#16a34a' }}>Storage:</strong> $0.016 per GB (approximately $8 per 500GB)
                </p>
                <p style={{ margin: 0 }}>
                  <strong style={{ color: '#16a34a' }}>Bandwidth:</strong> $0.003 per GB (approximately $3 per 1TB)
                </p>
              </div>
            </div>

            {/* FAQ 3 */}
            <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '8px', padding: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#e2e8f0', marginBottom: '12px' }}>
                How does metered billing work?
              </h3>
              <p style={{ color: '#d1d5db', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                Your usage is tracked automatically. At the end of each billing cycle, any overages 
                are calculated and added to your next invoice. You'll receive a detailed breakdown 
                of your usage and charges.
              </p>
            </div>

            {/* FAQ 4 */}
            <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '8px', padding: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#e2e8f0', marginBottom: '12px' }}>
                How does bandwidth tracking work?
              </h3>
              <p style={{ color: '#d1d5db', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                Bandwidth is tracked against the file uploader, not the downloader. When someone downloads your files, 
                it counts against your bandwidth limit. However, downloading your own files is free and doesn't count 
                towards your bandwidth usage.
              </p>
            </div>

            {/* FAQ 5 */}
            <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '8px', padding: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#e2e8f0', marginBottom: '12px' }}>
                Can I monitor my usage?
              </h3>
              <p style={{ color: '#d1d5db', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                Yes! Premium users can view their current storage and bandwidth usage in the 
                Profile page. You'll see real-time usage statistics and can track your consumption 
                throughout the billing period.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <UserRegistrationModal
        isOpen={showRegistrationModal}
        onClose={() => setShowRegistrationModal(false)}
        onSuccess={handleRegistrationSuccess}
        currentUserId={localStorage.getItem('fileshare_user_id')}
      />

      <CheckoutModal
        isOpen={showCheckoutModal}
        onClose={handleCheckoutClose}
        planId={selectedPlanId}
        userEmail={user?.email}
        userId={localStorage.getItem('fileshare_user_id')}
      />

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default PricingPage;